// filepath: /d:/Bridges/DDNS/ddnsmanager_react/src/manage/tableComponent.js
import React, { useState, useCallback, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, Typography } from '@mui/material';
import { MantineReactTable } from 'mantine-react-table';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconEdit, IconDownload, IconPlayerStop, IconPlayerPlay, IconTrash } from '@tabler/icons-react';
import '@mantine/core/styles.css';
import 'mantine-react-table/styles.css'; //make sure MRT styles were imported in your app root (once)
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons'; //replace free solid with your desired icon set
import '@fortawesome/fontawesome-svg-core/styles.css';
import { config } from '@fortawesome/fontawesome-svg-core';
import { MRT_Localization_JA } from 'mantine-react-table/locales/ja';
import { MRT_Localization_EN } from 'mantine-react-table/locales/en';

config.autoAddCss = false;


const TableComponent = ({ data, onEdit, onDelete, onDownload, onEnableDisable, onProjectSelect, onProjectclick, onAddProjectclick, onProjectEdit, onRefresh }) => {
    const inactivityTimeout = 1 * 60 * 1000;// 1 minutes default
    const [lastActivity, setLastActivity] = useState(Date.now());
    const { t, i18n } = useTranslation();
    const [confirmOpen, setConfirmOpen] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [errors, setErrors] = useState({});
    const [deleteNameValue, setdeleteNameValue] = useState('')
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [localization, setLocalization] = useState(MRT_Localization_EN);

    useEffect(() => {
        // Update localization based on the current language
        if (i18n.language === 'ja') {
            setLocalization(MRT_Localization_JA);
        } else {
            setLocalization(MRT_Localization_EN);
        }
    }, [i18n.language]);
    // Add refresh handler
    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await onRefresh();
        } finally {
            setIsRefreshing(false);
        }
    }, [onRefresh, setIsRefreshing]);
    const updateLastActivity = useCallback(() => {
        setLastActivity(Date.now());
    }, []);

    // Check inactivity and refresh
    const checkInactivity = useCallback(() => {
        const currentTime = Date.now();
        if (currentTime - lastActivity >= inactivityTimeout) {
            handleRefresh();
        }
    }, [lastActivity, inactivityTimeout, handleRefresh]);
    // Set up activity listeners
    useEffect(() => {
        const intervalId = setInterval(checkInactivity, 10000);
        return () => {
            clearInterval(intervalId);
        };
    }, [updateLastActivity, checkInactivity]);

    // Monitor API calls
    useEffect(() => {
        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            const response = await originalFetch(...args);
            updateLastActivity();
            return response;
        };

        return () => {
            window.fetch = originalFetch;
        };
    }, [updateLastActivity]);


    const validate = () => {
        const newErrors = {};
        if (selectedRow.subDomain) {
            if (deleteNameValue !== selectedRow.subDomain) {
                newErrors.subDomain = t('nameDoesNotMatch');
            }
        } else {
            if (deleteNameValue !== selectedRow.project) {
                newErrors.project = t('nameDoesNotMatch');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleDeleteClick = (row) => {
        setSelectedRow(row);
        if (row.subDomain) {
            setConfirmOpen("delete");
        }
        else {
            setConfirmOpen("delete-project");
        };
    };

    const handleEnableDisableClick = (row) => {
        setdeleteNameValue('');
        setSelectedRow(row);
        setConfirmOpen("enable-disable");
    };

    const handleConfirmClose = () => {
        setConfirmOpen(null);
    };

    const handleDownloadClick = (row) => {
        setSelectedRow(row);
        const timeoutValue = row.timeout || '3600';
        onDownload(row.subDomain, timeoutValue, row.id);
    };

    const handleConfirmDelete = () => {
        if (selectedRow) {
            if (validate()) {
                if (selectedRow.subDomain) {
                    onDelete(selectedRow.id, selectedRow.subDomain);
                }
                else {
                    onDelete(selectedRow.id, null, selectedRow.project);
                }

                setConfirmOpen(null);
                setSelectedRow(null);
            }
        }
    };

    const handleConfirmEnableDisable = () => {
        if (selectedRow) {
            selectedRow.isDisabled = !selectedRow.isDisabled;
            onEnableDisable(selectedRow);
        }
        setConfirmOpen(null);
        setSelectedRow(null);
    };

    const transformData = (rawData) => {
        const groupedData = rawData.reduce((acc, item) => {
            if (!acc[item.project]) {
                acc[item.project] = {
                    id: `${item.id}`,
                    project: item.project,
                    description: item.description || '',
                    subRows: []
                };
            }
            if (item.subDomain) {
                acc[item.project].subRows.push({
                    ...item,
                    id: item.id,
                    hasRecentActivity: hasRecentActivity(item.lastAccessedDate, item.timeout),
                    status: item.isDisabled ? 'gray' : hasRecentActivity(item.lastAccessedDate, item.timeout) ? 'green' : 'red',
                    statusTooltip: item.isDisabled ? t('DNS update is disabled') :
                        hasRecentActivity(item.lastAccessedDate, item.timeout) ?
                            t('DNS has been updated within the interval period.') :
                            t('No updates within the interval period.')
                });

                const subRows = acc[item.project].subRows.filter(row => row.isDisabled === false);
                const allActive = subRows.every(row => row.hasRecentActivity === true);
                const allInactive = subRows.every(row => row.hasRecentActivity === false);

                if (subRows.every(row => row.isDisabled === true)) {
                    acc[item.project].status = 'gray';
                    acc[item.project].statusTooltip = t('alldnsdisabled');
                }
                else if (allActive) {
                    acc[item.project].status = 'green';
                    acc[item.project].statusTooltip = t('alldnsgreen');
                }
                else if (allInactive) {
                    acc[item.project].status = 'red';
                    acc[item.project].statusTooltip = t('alldnsred');
                }
                else {
                    acc[item.project].status = 'orange';
                    acc[item.project].statusTooltip = t('orangedns');
                }

            }
            return acc;
        }, {});

        return Object.values(groupedData);
    };

    const hasRecentActivity = (lastAccessedDate, interval) => {
        const now = new Date();
        const lastAccessed = new Date(lastAccessedDate);
        const diffInMinutes = (now - lastAccessed) / 1000 / 60;
        return diffInMinutes <= interval / 60;
    };

    const columns = [
        {
            accessorKey: 'project',
            header: t('project'),
            Cell: ({ row }) => {
                if (row.original.ip !== undefined) {
                    return <div></div>;
                }
                return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            {row.original.project || 'no project'}
                        </div>
                    </div >
                )
            }
        },
        {
            accessorKey: 'status',
            header: t('status'),
            size: 120,
            Cell: ({ row }) => {
                return (
                    <Tooltip label={row.original.statusTooltip}>
                        <div style={{
                            height: '20px',
                            width: '20px',
                            backgroundColor: row.original.status,
                            borderRadius: '50%',
                            display: 'inline-block'
                        }} />
                    </Tooltip>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
        },
        {
            accessorKey: 'subDomain',
            header: t('subdomain'),
            Cell: ({ row }) => {
                if (!row.original.subRows) {
                    return row.original.subDomain?.split('.')[0] || '';
                }
                return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {row.original.project && (
                            <Tooltip label={t('Add Subdomain')}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        onProjectSelect(row.original.project);
                                        onProjectclick();
                                    }}
                                >
                                    {t('add')}
                                </Button>
                            </Tooltip>
                        )}
                    </div >
                )
            },
        },
        {
            accessorKey: 'ip',
            header: t('ip'),
            enableHiding: true,
        },
        {
            accessorKey: 'description',
            header: t('description'),
            Cell: ({ row }) => {
                return (
                    `${row.original.description}`
                )
            }
        },
        {
            accessorKey: 'timeout',
            header: t('updateInterval'),
            Cell: ({ row }) => {
                if (!row.original.subRows) {
                    return `${row.original.timeout} secs`;
                }
                return null;
            },
        },
        {
            accessorKey: 'lastAccessedDate',
            header: t('lastUpdated'),
            Cell: ({ row }) => {
                if (!row.original.subRows) {
                    return new Date(row.original.lastAccessedDate).toLocaleString();
                }
                return null;
            },
        },
        {
            accessorKey: 'actions',
            header: t('actions'),
            enableColumnFilter: false,
            enableSorting: false,
            Cell: ({ row }) => {
                if (!row.original.subRows) {
                    return (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Tooltip label={t('Edit')}>
                                <ActionIcon color="blue" onClick={() => onEdit(row.original)}>
                                    <IconEdit />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('Download Script')}>
                                <ActionIcon color="grape"
                                    onClick={() => handleDownloadClick(row.original)}>
                                    <IconDownload />
                                </ActionIcon>
                            </Tooltip>
                            {!row.original.isDisabled && (
                                <Tooltip label={t('Disable')}>
                                    <ActionIcon color="red"
                                        onClick={() => handleEnableDisableClick(row.original)}>
                                        <IconPlayerStop />
                                    </ActionIcon>
                                </Tooltip>
                            )}
                            {row.original.isDisabled && (
                                <>
                                    <Tooltip label={t('Enable')}>
                                        <ActionIcon color="green"
                                            onClick={() => handleEnableDisableClick(row.original)}>
                                            <IconPlayerPlay />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label={t('Delete')}>
                                        <ActionIcon color="red"
                                            onClick={() => handleDeleteClick(row.original)}>
                                            <IconTrash />
                                        </ActionIcon>
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    );
                }
                return (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Tooltip label={t('Edit')}>
                            <ActionIcon color="blue" onClick={() => onProjectEdit(row.original)}>
                                <IconEdit />
                            </ActionIcon>
                        </Tooltip>
                        {row.original.subRows.length === 0 && (
                            <Tooltip label={t('Delete Project')}>
                                <ActionIcon color="red"
                                    onClick={() => handleDeleteClick(row.original)}
                                >
                                    <IconTrash />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        },
    ];
    return (
        <>
            <style>{`
            .MRT_ExpandAllButton-module_root__gkBZD {
                margin-left: 0 !important;
            }
            `}
            </style>
            <MantineReactTable
                localization={localization}
                state={{ isLoading: isRefreshing }}
                initialState={{
                    density: 'xs'
                }}
                enableFullScreenToggle={false}
                mantineTableProps={{
                    withBorder: true,
                    withColumnBorders: true,
                    highlightOnHover: true,
                    striped: false,
                    styles: {
                        root: {
                        },
                        p: {
                            '--mantine-font-family': "'Roboto', 'Hiragino Sans', 'Yu Gothic UI', 'Hiragino Kaku Gothic ProN', 'sans-serif'",
                        },
                        th: {
                            '--header-mrt_row_expand-size': '50',
                            '--mantine-font-family': "'Roboto', 'Hiragino Sans', 'Yu Gothic UI', 'Hiragino Kaku Gothic ProN', 'sans-serif'",
                        },
                        td: {
                            '--col-mrt_row_expand-size': '50',
                            '--mantine-font-family': "'Roboto', 'Hiragino Sans', 'Yu Gothic UI', 'Hiragino Kaku Gothic ProN', 'sans-serif'",
                        }
                    }
                }}
                autoResetExpanded={false}
                collapseOnDataChange={false}
                enableExpanding={true}
                enableColumnResizing={true}
                columns={columns}
                data={transformData(data)}
                mantineExpandAllButtonProps={({ table }) => ({
                    color: 'gray',
                    variant: 'subtle',
                    size: 'sm',
                    showSkeleton: true,
                    showloading: true,
                    // Custom expand all/collapse all icons
                    children: !table.getIsSomeRowsExpanded() ? (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
                            <FontAwesomeIcon icon={faChevronRight} />
                            <FontAwesomeIcon icon={faChevronRight} />
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <FontAwesomeIcon icon={faChevronDown} style={{ marginBottom: '-10px' }} />
                            <FontAwesomeIcon icon={faChevronDown} />
                        </div>
                    ),
                    // Add tooltip
                    title: table.getIsSomeRowsExpanded() ?
                        t('Collapse All') :
                        t('Expand All'),
                })}
                displayColumnDefOptions={{
                    'mrt-row-expand': {
                        Cell: ({ row, table }) => row.getLeafRows().length > 0 ? (
                            <ActionIcon
                                onClick={(e) => {
                                    e.stopPropagation();
                                    row.toggleExpanded();
                                }}
                                size="sm"
                                variant="subtle"
                                color="gray"
                            >
                                {row.getIsExpanded() ? (
                                    <FontAwesomeIcon icon={faChevronDown} />
                                ) : (
                                    <FontAwesomeIcon icon={faChevronRight} />
                                )}
                            </ActionIcon>
                        ) : (<></>),
                    },
                }}
                renderTopToolbarCustomActions={() => (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                onProjectSelect("");
                                onAddProjectclick();
                            }}
                            sx={{
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#1565c0'
                                }
                            }}
                        >
                            <span style={{ marginRight: '4px' }}>+</span>
                            {t('Project')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRefresh}
                            disabled={isRefreshing}
                            sx={{
                                backgroundColor: '#1976d2',
                                '&:hover': {
                                    backgroundColor: '#1565c0'
                                }
                            }}
                        >
                            {isRefreshing ? t('Refreshing') : t('Refresh')}
                        </Button>
                    </div >
                )}
            />

            < Dialog open={confirmOpen && confirmOpen.startsWith("delete")} onClose={handleConfirmClose} >
                <DialogTitle>{t('Confirm Delete')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" align="center">
                        {localStorage.getItem("language") !== "ja" ?
                            <p>{t('Are you sure you want to delete')} <strong>{selectedRow?.subDomain ? selectedRow.subDomain : selectedRow?.project}</strong>.
                                {t('Type the name of the subdomain to confirm')}
                            </p>
                            :
                            <p><strong>{selectedRow?.subDomain ? selectedRow.subDomain : selectedRow?.project}</strong> {t('Are you sure you want to delete')}
                                {t('Type the name of the subdomain to confirm')}
                            </p>
                        }
                    </Typography>
                    <TextField
                        margin="dense"
                        name="name"
                        label=""
                        fullWidth
                        value={deleteNameValue || ''}
                        onChange={(event) => {
                            setdeleteNameValue(event.target.value);
                        }}
                        required={true}
                        error={selectedRow?.subDomain ? !!errors.subDomain : !!errors.project}
                        helperText={selectedRow?.subDomain ? errors.subDomain : errors.project}
                    >
                    </TextField>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleConfirmClose} color="primary">
                        {t('Cancel')}
                    </Button>
                    <Button onClick={handleConfirmDelete} color="secondary">
                        {t('Delete')}
                    </Button>
                </DialogActions>
            </Dialog >

            <Dialog open={confirmOpen === "enable-disable"} onClose={handleConfirmClose}>
                <DialogTitle>{`${!!selectedRow?.isDisabled === true ? t('Enable') : t('Disable')} ${selectedRow?.subDomain}`}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" align="center">
                        <p>{`${t('Are you sure you want to')} ${!!selectedRow?.isDisabled === true ? t('enable') : t('disable')} `}
                            <strong> {selectedRow?.subDomain}</strong>?
                        </p>
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmClose} color="primary">
                        {t('Cancel')}
                    </Button>
                    <Button onClick={handleConfirmEnableDisable} color="secondary">
                        {!!selectedRow?.isDisabled === true ? t('Enable') : t('Disable')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TableComponent;